<template>
  <BaseDialog
    ref="dialog"
    contentClass="tender-zones"
    :show-actions="true"
    :persistent="!hasNoChanges"
    :on-close="closeModal"
    @closeModal="closeModal"
  >
    <template slot="title">{{ $t('search-filters.zone.name') }}</template>
    <template slot="content">
      <div class="tender-zones__content">
        <ZoneTree ref="zoneTree" :searchFilters="searchFilters" @onchange="onZonesChange" />
        <div class="tender-zones__unlocated">
          <FormCheckbox
            :label="$t('search-filters.zone.include-unlocated')"
            :value="tempIncludeUnlocated"
            :disabled="!hasZones"
            @input="tempIncludeUnlocated = $event"
            size="small"
          />
          <p class="tender-zones__unlocated-hint">{{ $t('search-filters.zone.include-unlocated-hint') }}</p>
        </div>
      </div>
    </template>
    <template slot="actions">
      <div class="tender-zones__actions">
        <BaseButton isTransparent isUnderline isSquare @click.native="onClearAll">
          {{ $t('dialog.filters.advanced.clear-all') }}
        </BaseButton>
        <BaseButton :disabled="hasNoChanges" @click.native="applyChange">
          {{ $t('dialog.filters.advanced.apply-all') }}
        </BaseButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import FormCheckbox from '@/components/form/FormCheckbox';
import ZoneTree from '@/components/page-tenders/ZoneTree.vue';
import { areZonesEqual } from '@/util/zones-util';

export default {
  name: 'TenderZonesDialog',
  components: { BaseButton, BaseDialog, FormCheckbox, ZoneTree },
  props: {
    searchFilters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      tempZones: {},
      // Snapshot of the zones when the dialog opened — the baseline for detecting net changes.
      originalZones: {},
      tempIncludeUnlocated: false,
      originalIncludeUnlocated: false,
    };
  },
  computed: {
    hasNoChanges() {
      // Compare against the open-time snapshot so reverting to the original selection re-disables
      // Apply, instead of latching "dirty" forever. Order-independent — the tree may rebuild the
      // zones object with different key/array order for the same selection.
      return (
        areZonesEqual(this.tempZones, this.originalZones) && this.tempIncludeUnlocated === this.originalIncludeUnlocated
      );
    },
    hasZones() {
      return Object.keys(this.tempZones || {}).length > 0;
    },
  },
  methods: {
    onZonesChange(zones) {
      // ZoneTree emits the same object reference mutated in place, so assigning it directly would be
      // a no-op for Vue (identical reference → no reactivity) and the dirty computed would go stale.
      // Cloning forces a new reference so hasNoChanges recomputes.
      this.tempZones = JSON.parse(JSON.stringify(zones || {}));
    },
    applyChange() {
      // The option only has an effect with a zone selection, so it is saved off without one.
      this.$emit('apply', { zones: this.tempZones, includeUnlocated: this.hasZones && this.tempIncludeUnlocated });
      this.closeModal();
    },
    onClearAll() {
      this.$refs.zoneTree.clearSelection();
      this.tempZones = {};
      this.tempIncludeUnlocated = false;
    },
    closeModal() {
      this.$emit('close-dialog');
    },
  },
  created() {
    this.originalZones = JSON.parse(JSON.stringify(this.searchFilters.zones || {}));
    this.tempZones = JSON.parse(JSON.stringify(this.searchFilters.zones || {}));
    this.originalIncludeUnlocated = !!this.searchFilters.includeUnlocated;
    this.tempIncludeUnlocated = this.originalIncludeUnlocated;
    this.$nextTick(() => {
      this.$refs.dialog.isModalOpen = true;
    });
  },
};
</script>

<style lang="scss">
// Right-side drawer (BaseDialog default), widened to match the keywords drawer.
.base-dialog__tender-zones {
  width: 580px;
  max-width: 100%;

  .base-dialog__header {
    border-bottom: 1px solid $color-border;
  }

  // Drawer body surface is pure white (overrides BaseDialog's neutral-pale
  // header+content default).
  .base-dialog__header + .base-dialog__content {
    background-color: $color-white;
    padding: 24px 32px;
  }

  // ZoneTree renders its own section title (needed inside PageExplore's advanced
  // dialog); the drawer header already shows it, so hide the duplicate here.
  .zone-tree__title {
    display: none;
  }

  // The hierarchy (bold parents, indented provinces, guide line) lives in
  // ZoneTree itself. The drawer only needs to shrink the provinces into two
  // compact columns — the shared 240px width would overflow the narrow body.
  .zone-tree__regions .zone-tree__checkbox {
    width: 47%;
    padding: 8px 0;
  }

  .tender-zones__unlocated {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid $color-border;
  }

  .tender-zones__unlocated-hint {
    margin: 4px 0 0 28px;
    font-size: 12px;
    color: $color-text-muted;
  }

  .base-dialog__actions {
    padding: 20px 32px;
    border-top: 1px solid $color-border;
  }

  // Phones: one region per line (ZoneTree), not the two compact columns; the screen's gutters, and
  // the footer's buttons on one line each.
  @include screen-below('tablet') {
    .base-dialog__header + .base-dialog__content {
      padding: $space-md;
    }

    .zone-tree__regions .zone-tree__checkbox {
      width: 100%;
      padding: 0;
    }

    .base-dialog__actions {
      padding: $space-3 $space-md;
    }

    .tender-zones__actions {
      gap: $space-3;

      .base-button__element {
        white-space: nowrap;
      }
    }
  }
}

.tender-zones {
  &__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}
</style>
